import { AlertTriangle } from 'lucide-react';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import type { AssetArc } from '@/types/oscar';
import { arcBadges, formatMemory } from './arcFormat';

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt className="font-semibold text-darkGrey/70">{label}</dt>
      <dd className="break-words">{value || '—'}</dd>
    </>
  );
}

/**
 * What Azure Arc says about a server, read-only.
 *
 * <p>Servers added to Arc for Defender reach Intune only as a thin record. Oscar reads Arc itself and creates
 * the Server asset from it (Anthony, 01/11/2026), so this is where the Arc-owned facts show: connection state,
 * hardware, OS edition, agent version and whether the Defender sensor is deployed.</p>
 *
 * <p>⚠ DEFENDER "INSTALLED" IS NOT "HEALTHY". It means the MDE.Windows extension is on the machine. It says
 * nothing about whether Defender is reporting or what it makes of the server, and the page says so rather than
 * letting a green tick read as a clean bill of health.</p>
 *
 * <p>⚠ Nothing here is edited by hand: the Arc sync overwrites it every six hours.</p>
 */
export function ArcBadges({ arc }: { arc: AssetArc }) {
  return (
    <ul aria-label="Azure Arc" className="mt-1 flex flex-wrap gap-2">
      {arcBadges(arc).map((b) => (
        <li
          key={b.text}
          className={b.tone === 'ok'
            ? 'rounded-full border border-navy/30 bg-navy/5 px-2.5 py-0.5 text-xs font-semibold text-navy'
            : 'rounded-full border border-orange/40 bg-orange/10 px-2.5 py-0.5 text-xs font-semibold text-navy'}
        >
          {b.text}
        </li>
      ))}
    </ul>
  );
}

export function AssetArcTab({ arc }: { arc: AssetArc }) {
  const tags = (() => {
    if (!arc.TagsJson) return null;
    try {
      const o = JSON.parse(arc.TagsJson) as Record<string, string>;
      const text = Object.entries(o).map(([k, v]) => `${k}: ${v}`).join(' · ');
      return text || null;
    } catch {
      return null;
    }
  })();

  return (
    <div className="space-y-3">
      {!arc.IsPresent && (
        <p role="alert" className="flex items-start gap-2 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span>
            This server is no longer in Azure Arc{arc.MissingSince ? ` (since ${formatDate(arc.MissingSince)})` : ''}. Its asset
            has not been changed: whether it was decommissioned is for a person to decide.
          </span>
        </p>
      )}

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded border border-border bg-white p-3 text-sm text-darkGrey">
        <Row label="Arc connection" value={arc.ArcStatus ? `${arc.ArcStatus}${arc.LastStatusChange ? ` (changed ${formatDateTime(arc.LastStatusChange)})` : ''}` : null} />
        <Row label="Operating system" value={[arc.OsSku, arc.OsVersion].filter(Boolean).join(' · ') || null} />
        <Row label="Domain" value={arc.DomainName} />
        <Row label="Make and model" value={[arc.Manufacturer, arc.Model].filter(Boolean).join(' ') || null} />
        <Row label="Hypervisor" value={arc.HypervisorType} />
        <Row label="Processor" value={arc.CpuModel ? `${arc.CpuModel}${arc.LogicalCores ? ` · ${arc.LogicalCores} cores` : ''}` : (arc.LogicalCores ? `${arc.LogicalCores} cores` : null)} />
        <Row label="Memory" value={arc.MemoryBytes ? formatMemory(arc.MemoryBytes) : null} />
        <Row label="TPM" value={arc.TpmPresent === null ? null : (arc.TpmPresent ? 'Present' : 'Not present')} />
        <Row label="Arc agent" value={arc.AgentVersion} />
        <Row label="Resource group" value={[arc.ResourceGroup, arc.Location].filter(Boolean).join(' · ') || null} />
        <Row label="Tags" value={tags} />
        <Row label="Last synced from Arc" value={formatDateTime(arc.LastSyncDate)} />
        <Row label="First seen in Arc" value={formatDateTime(arc.FirstSeenDate)} />
        <Row label="How it got here" value={arc.CreatedAsset ? 'Oscar created this asset from Azure Arc' : 'An existing asset, linked to its Arc machine'} />
      </dl>

      <div className="rounded border border-border bg-white p-3 text-sm text-darkGrey">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">Defender</h2>
        {arc.DefenderInstalled ? (
          <p className="mt-1">
            Sensor deployed (Defender extension{arc.DefenderVersion ? ` ${arc.DefenderVersion}` : ''}
            {arc.DefenderState ? `, ${arc.DefenderState}` : ''}).
          </p>
        ) : (
          <p className="mt-1 font-semibold text-orange">The Defender extension is not installed on this server.</p>
        )}
        <p className="mt-1 text-xs text-darkGrey/70">
          This says the sensor is installed, not that Defender is healthy or what risk it reports. Read that in the Defender portal.
        </p>
      </div>

      <p className="text-xs text-darkGrey/60">Read from Azure Arc and refreshed every six hours. It is not edited here.</p>
    </div>
  );
}
